import React, { useMemo, useState } from 'react';

import {
  BuildingsIcon,
  CheckIcon,
  CircleNotchIcon,
  GlobeIcon,
  QuestionIcon,
  SignOutIcon,
  UserCircleIcon,
} from '@phosphor-icons/react';
import { useQueryClient } from '@tanstack/react-query';
import { motion } from 'framer-motion';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';

import { useAppSelector } from '@/app/hooks';
import { useFeatureFlag } from '@/app/services/features';
import {
  Accordion,
  AccordionContent,
  AccordionItem,
  AccordionTrigger,
} from '@/components/ui/accordion';
import {
  Dialog,
  DialogBody,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
  DialogTrigger,
} from '@/components/ui/dialog';
import {
  Tooltip,
  TooltipContent,
  TooltipTrigger,
} from '@/components/ui/tooltip';
import { ZapperDialog } from '@/components/zapper';
import { getAllLanguages, getLanguageByKey } from '@/languages';
import { clearAuthTokens, getAccessToken } from '@/lib/tokenStorage';
import { cn } from '@/lib/utils';
import {
  hasPermission,
  useCurrentUser,
  useSwitchDatabase,
} from '@/services/auth/util';

import NucleusLogo from '/images/NucleusLogo.png';

const LAST_USED_DB_KEY = 'tog_last_used_database';

export type NavToggleState = 'open' | 'collapse' | 'close';

type AppHeaderProps = {
  toggleDrawer: () => void;
  /** What the next hamburger click will do */
  navToggleState?: NavToggleState;
};

export const AppHeader: React.FC<AppHeaderProps> = ({
  toggleDrawer,
  navToggleState = 'open',
}) => {
  const { t, i18n } = useTranslation();
  const { me } = useAppSelector((s) => s.authentication);
  const navigate = useNavigate();
  const queryClient = useQueryClient();
  const { data: currentUser } = useCurrentUser();
  const [switchDb, { isPending: isSwitchingDb }] = useSwitchDatabase();
  const [isDropdownOpen, setIsDropdownOpen] = useState(false);
  const [switchingCompanyId, setSwitchingCompanyId] = useState<string | null>(
    null,
  );

  const languages = getAllLanguages();
  const currentLanguage = getLanguageByKey(
    i18n.language ?? i18n.resolvedLanguage,
  );

  const currentCompanyId = useMemo(() => {
    const storedId = localStorage.getItem(LAST_USED_DB_KEY);
    if (storedId) return storedId;

    const token = getAccessToken();
    if (!token) return null;
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      const decoded = JSON.parse(jsonPayload);
      return decoded?.active_company_id || null;
    } catch {
      return null;
    }
  }, []);

  const currentCompanyName = useMemo(() => {
    if (!currentUser?.companies?.length) return null;
    if (!currentCompanyId) return currentUser.companies[0]?.name || null;

    const company = currentUser.companies.find(
      (c) => c.companyId === currentCompanyId,
    );
    return company?.name || currentUser.companies[0]?.name || null;
  }, [currentUser?.companies, currentCompanyId]);

  const isTogActive = useMemo(
    () =>
      currentUser?.companies?.some((c) => c.code === 'TOG' && c.isActive) ??
      false,
    [currentUser?.companies],
  );

  const canSwitchDatabase = useMemo(() => {
    return hasPermission(currentUser, 'DATABASE.SWITCH');
  }, [currentUser]);

  const handleLogout = () => {
    clearAuthTokens();
    localStorage.removeItem(LAST_USED_DB_KEY);
    window.location.href = '/login';
  };

  const handleSwitchCompany = async (companyId: string) => {
    if (companyId === currentCompanyId || isSwitchingDb) return;
    try {
      setSwitchingCompanyId(companyId);
      await switchDb(companyId);
      localStorage.setItem(LAST_USED_DB_KEY, companyId);
      await queryClient.invalidateQueries({ queryKey: ['currentUser'] });
      window.location.reload();
    } catch (error) {
      console.error('Failed to switch company:', error);
      setSwitchingCompanyId(null);
    }
  };

  const handleLanguageChange = (langValue: string) => {
    setIsDropdownOpen(false);
    i18n.changeLanguage(langValue);
  };

  const enableZapperDialog = useFeatureFlag('Bug_Reporter');

  return (
    <header className="preflight flex h-[60px] items-center justify-between border-b border-[var(--surface-border)] bg-[var(--surface-ground)] pl-4 sm:pl-6">
      <div className="flex items-center">
        <button
          type="button"
          aria-label={
            navToggleState === 'collapse'
              ? t('nav.collapseNavigation', 'Collapse navigation')
              : navToggleState === 'close'
                ? t('nav.closeNavigation', 'Close navigation')
                : t('nav.openNavigation', 'Open navigation')
          }
          onClick={toggleDrawer}
          className="flex size-9 cursor-pointer items-center justify-center rounded-md border-none bg-transparent text-[var(--text-color)] transition-colors hover:bg-[var(--surface-hover)]"
        >
          <NavToggleIcon state={navToggleState} />
        </button>

        <div className="mx-6 hidden h-[30px] w-[2px] bg-[var(--surface-border)] sm:block" />

        <img
          src={NucleusLogo}
          alt="Nucleus"
          role="button"
          onClick={() => navigate('/')}
          className="ml-3 h-30 cursor-pointer select-none sm:ml-0"
        />
      </div>

      <div className="flex items-center gap-3 pr-4 sm:gap-4 sm:pr-6">
        <HelpDialog />

        {enableZapperDialog && isTogActive && <ZapperDialog />}

        <div
          className="relative"
          onMouseEnter={() => setIsDropdownOpen(true)}
          onMouseLeave={() => setIsDropdownOpen(false)}
        >
          <div className="flex cursor-pointer flex-col items-center justify-center text-[0.5rem] font-medium text-[var(--text-primary)]">
            <div className="flex flex-col items-center justify-center">
              <UserCircleIcon className="size-5" />
              <span>{me?.displayName}</span>
            </div>
            {currentCompanyName && (
              <span className="text-[var(--text-secondary)]">
                {currentCompanyName}
              </span>
            )}
            <span
              className={
                me?.environment === 'TOG Training'
                  ? 'bg-red-600 px-1 text-white'
                  : undefined
              }
            >
              {me?.environment}
            </span>
          </div>

          <div
            className={cn(
              'absolute top-full right-0 z-50 mt-2 min-w-[180px] origin-top-right rounded-lg border-0 bg-white p-1.5 shadow-lg transition-all duration-200 ease-out',
              isDropdownOpen
                ? 'visible translate-y-0 opacity-100'
                : 'invisible -translate-y-2 opacity-0',
            )}
          >
            {canSwitchDatabase &&
              currentUser?.companies &&
              currentUser.companies.length > 1 && (
                <div className="group relative">
                  <button className="flex w-full items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2.5 text-left text-sm font-medium text-zinc-700 transition-colors duration-150 hover:bg-zinc-100">
                    <BuildingsIcon className="size-4" />
                    {currentCompanyName || t('header.switchCompany')}
                  </button>
                  <div className="invisible absolute top-0 right-full mr-1 min-w-[180px] rounded-lg border-0 bg-white p-1.5 opacity-0 shadow-lg transition-all duration-150 group-hover:visible group-hover:opacity-100">
                    {currentUser.companies.map((company) => {
                      const isCurrentCompany =
                        company.companyId === currentCompanyId;
                      const isSwitching =
                        switchingCompanyId === company.companyId;
                      return (
                        <button
                          key={company.companyId}
                          onClick={() => handleSwitchCompany(company.companyId)}
                          disabled={isSwitchingDb}
                          className={`flex w-full items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-[var(--primary-color)] hover:text-white disabled:cursor-not-allowed disabled:opacity-50 ${
                            isCurrentCompany
                              ? 'font-semibold text-[var(--primary-color)]'
                              : 'text-zinc-700'
                          }`}
                        >
                          {isSwitching ? (
                            <CircleNotchIcon className="size-3 animate-spin" />
                          ) : isCurrentCompany ? (
                            <CheckIcon className="size-3" />
                          ) : null}
                          <span
                            className={
                              isSwitching || isCurrentCompany ? '' : 'ml-4'
                            }
                          >
                            {company.name}
                          </span>
                        </button>
                      );
                    })}
                  </div>
                </div>
              )}
            <div className="group relative">
              <button className="flex w-full items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2.5 text-left text-sm font-medium text-zinc-700 transition-colors duration-150 hover:bg-zinc-100">
                <GlobeIcon className="size-4" />
                {currentLanguage?.label || t('header.language')}
              </button>
              <div className="invisible absolute top-0 right-full mr-1 min-w-[160px] rounded-lg border-0 bg-white p-1.5 opacity-0 shadow-lg transition-all duration-150 group-hover:visible group-hover:opacity-100">
                {languages.map((lang) => (
                  <button
                    key={lang.value}
                    onClick={() => handleLanguageChange(lang.value)}
                    className={`flex w-full items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2 text-left text-sm transition-colors duration-150 hover:bg-[var(--primary-color)] hover:text-white ${
                      currentLanguage?.value === lang.value
                        ? 'font-semibold text-[var(--primary-color)]'
                        : 'text-zinc-700'
                    }`}
                  >
                    {lang.label}
                    {currentLanguage?.value === lang.value && (
                      <CheckIcon className="ml-auto size-3" />
                    )}
                  </button>
                ))}
              </div>
            </div>
            <div className="my-1.5 h-px border-0 bg-zinc-100" />
            <button
              onClick={handleLogout}
              className="flex w-full items-center gap-3 rounded-md border-0 bg-transparent px-3 py-2.5 text-left text-sm font-medium text-zinc-700 transition-colors duration-150 hover:bg-red-500 hover:text-white"
            >
              <SignOutIcon className="size-4" />
              {t('header.logOut')}
            </button>
          </div>
        </div>
      </div>
    </header>
  );
};

function HelpDialog() {
  const { t } = useTranslation();

  return (
    <Dialog>
      <Tooltip>
        <DialogTrigger
          render={
            <TooltipTrigger>
              <QuestionIcon className="size-5" />
            </TooltipTrigger>
          }
        />
        <TooltipContent>{t('help.title')}</TooltipContent>
      </Tooltip>
      <DialogContent className="md:max-w-5xl">
        <DialogHeader>
          <DialogTitle>{t('help.title')}</DialogTitle>
        </DialogHeader>
        <DialogBody>
          <Accordion
            multiple
            defaultValue={[
              'ctrl-click',
              'filtering',
              'sorting',
              'range-selection',
              'reset-columns',
            ]}
          >
            <AccordionItem value="ctrl-click">
              <AccordionTrigger className="text-lg">
                {t('help.ctrlClick')}
              </AccordionTrigger>
              <AccordionContent>
                <p className="px-4 py-2">{t('help.ctrlClickDesc')}</p>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="filtering">
              <AccordionTrigger className="text-lg">
                {t('help.filtering')}
              </AccordionTrigger>
              <AccordionContent>
                <div className="mt-3 space-y-3">
                  <div>
                    <h5 className="font-medium">
                      {t('help.filteringColumnMenu')}
                    </h5>
                    <p className="px-4 py-2">
                      {t('help.filteringColumnMenuDesc')}
                    </p>
                    <div className="flex justify-around gap-2">
                      <img
                        src="/images/help/grid-menu.png"
                        alt={t('help.filteringColumnMenu')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h5 className="font-medium">{t('help.filteringText')}</h5>
                    <p className="px-4 py-2">{t('help.filteringTextDesc')}</p>
                    <div className="flex justify-around gap-2">
                      <img
                        src="/images/help/grid-menu-text-open.png"
                        alt={t('help.filteringText')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                      <img
                        src="/images/help/grid-menu-text-options.png"
                        alt={t('help.filteringText')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h5 className="font-medium">{t('help.filteringDate')}</h5>
                    <p className="px-4 py-2">{t('help.filteringDateDesc')}</p>
                    <div className="flex justify-around gap-2">
                      <img
                        src="/images/help/grid-menu-date-open.png"
                        alt={t('help.filteringDate')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                      <img
                        src="/images/help/grid-menu-date-options.png"
                        alt={t('help.filteringDate')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h5 className="font-medium">{t('help.filteringSet')}</h5>
                    <p className="px-4 py-2">{t('help.filteringSetDesc')}</p>
                    <div className="flex justify-around gap-2">
                      <img
                        src="/images/help/grid-menu-set-open.png"
                        alt={t('help.filteringSet')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="sorting">
              <AccordionTrigger className="text-lg">
                {t('help.sorting')}
              </AccordionTrigger>
              <AccordionContent>
                <p className="px-4 py-2">{t('help.sortingDesc')}</p>
                <div className="mt-3 space-y-3">
                  <div>
                    <h5 className="text-sm font-medium">
                      {t('help.sortingMenu')}
                    </h5>
                    <p className="px-4 py-2">{t('help.sortingMenuDesc')}</p>
                    <div className="flex justify-around gap-2">
                      <img
                        src="/images/help/grid-menu-sort-open.png"
                        alt={t('help.sortingMenu')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">
                      {t('help.sortingAsc')}
                    </h5>
                    <p className="px-4 py-2">{t('help.sortingAscDesc')}</p>
                    <div className="flex justify-around gap-2">
                      <img
                        src="/images/help/grid-menu-sort-asc.png"
                        alt={t('help.sortingAsc')}
                        loading="lazy"
                        className="h-auto rounded-md border object-contain"
                      />
                    </div>
                  </div>
                  <div>
                    <h5 className="text-sm font-medium">
                      {t('help.sortingDescending')}
                    </h5>
                    <p className="px-4 py-2">
                      {t('help.sortingDescendingDesc')}
                    </p>
                    <div className="flex justify-around gap-2">
                      <img
                        src="/images/help/grid-menu-sort-desc.png"
                        alt={t('help.sortingDescending')}
                        loading="lazy"
                        className="mt-2 h-auto rounded-md border object-contain"
                      />
                    </div>
                  </div>
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="range-selection">
              <AccordionTrigger className="text-lg">
                {t('help.rangeSelection')}
              </AccordionTrigger>
              <AccordionContent>
                <p className="px-4 py-2">{t('help.rangeSelectionDesc')}</p>
                <div className="flex justify-around gap-2">
                  <img
                    src="/images/help/grid-select-1.png"
                    alt={t('help.rangeSelection')}
                    loading="lazy"
                    className="h-auto rounded-md border object-contain"
                  />
                  <img
                    src="/images/help/grid-select-2.png"
                    alt={t('help.rangeSelection')}
                    loading="lazy"
                    className="h-auto rounded-md border object-contain"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
            <AccordionItem value="reset-columns">
              <AccordionTrigger className="text-lg">
                {t('help.resetColumns')}
              </AccordionTrigger>
              <AccordionContent>
                <p className="px-4 py-2">{t('help.resetColumnsDesc')}</p>
                <div className="flex justify-around gap-2">
                  <img
                    src="/images/help/grid-menu-reset.png"
                    alt={t('help.resetColumns')}
                    loading="lazy"
                    className="h-auto rounded-md border object-contain"
                  />
                </div>
              </AccordionContent>
            </AccordionItem>
          </Accordion>
        </DialogBody>
        <DialogFooter showCloseButton />
      </DialogContent>
    </Dialog>
  );
}

const NAV_TOGGLE_LINES: Record<
  NavToggleState,
  [number, number, number, number, number][]
> = {
  // x1, y1, x2, y2, opacity
  open: [
    [4, 6, 20, 6, 1],
    [4, 12, 20, 12, 1],
    [4, 18, 20, 18, 1],
  ],
  collapse: [
    [4, 12, 10, 6, 1],
    [4, 12, 20, 12, 1],
    [4, 12, 10, 18, 1],
  ],
  close: [
    [6, 6, 18, 18, 1],
    [12, 12, 12, 12, 0],
    [6, 18, 18, 6, 1],
  ],
};

function NavToggleIcon({ state }: { state: NavToggleState }) {
  return (
    <svg
      viewBox="0 0 24 24"
      className="size-6"
      fill="none"
      stroke="currentColor"
      strokeWidth={2}
      strokeLinecap="round"
      aria-hidden
    >
      {NAV_TOGGLE_LINES[state].map(([x1, y1, x2, y2, opacity], i) => (
        <motion.line
          key={i}
          initial={false}
          animate={{ x1, y1, x2, y2, opacity }}
          transition={{ duration: 0.28, ease: [0.32, 0.72, 0, 1] }}
        />
      ))}
    </svg>
  );
}
